import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Localization from "aldoo-ra/Localization"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { showNotification } from "aldoo-ra/Notification"
import { ActivateLicense } from "aldoo-ra/Admin/ProductLicensing/api"
import Value from "aldoo-ra/Value"

const LicenseActivation = ({ onActivationSuccess }) => {
  const [licenseCode, setLicenseCode] = useState("")
  const [isActivating, setIsActivating] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const { t } = Localization()
  const texts = TextBundle({ id: "activate_license" })
  
  // Get current user's device ID from credentials
  const [userCredentials] = Value("user-credentials")

  // Clean up any existing fake device IDs from localStorage (one-time cleanup)
  React.useEffect(() => {
    // Remove the fake device ID that was incorrectly generated
    const existingDeviceId = localStorage.getItem('deviceId')
    if (existingDeviceId && existingDeviceId.startsWith('web-')) {
      localStorage.removeItem('deviceId')
    }
  }, [])

  // Handle license activation
  const handleActivation = async () => {
    // Clear previous error
    setErrorMessage("")
    
    if (!licenseCode.trim()) {
      setErrorMessage(texts?.license_code_required_error || "Please enter a license code")
      return
    }

    setIsActivating(true)
    try {
      // Use current user's device ID from credentials
      const deviceId = userCredentials?.deviceID
      
      const response = await ActivateLicense({
        code: licenseCode.trim(),
        deviceId: deviceId
      })

      if (response.result) {
        showNotification({
          message: texts?.activation_success_message || "You've successfully activated your license!",
          className: "bg-success text-white"
        })
        setLicenseCode("")
        setErrorMessage("")
        // Call callback to switch to license info view
        if (onActivationSuccess) {
          onActivationSuccess()
        }
      } else {
        // Extract error message from response
        let errorMsg = texts?.license_activation_error || "An error occurred while activating the license"
        
        if (response.message) {
          errorMsg = response.message
        } else if (response.error) {
          // If there's an error code but no message, use a generic message
          errorMsg = texts?.license_activation_fail || "Failed to activate license"
        }
        
        setErrorMessage(errorMsg)
      }
    } catch (error) {
      console.error('Error activating license:', error)
      
      // Handle different types of errors
      let errorMsg = texts?.license_activation_error || "An error occurred while activating the license"
      
      if (error.response && error.response.data) {
        const responseData = error.response.data
        if (responseData.message) {
          errorMsg = responseData.message
        } else if (responseData.error) {
          errorMsg = texts?.license_activation_fail || "Failed to activate license"
        }
      } else if (error.message) {
        errorMsg = error.message
      }
      
      setErrorMessage(errorMsg)
    } finally {
      setIsActivating(false)
    }
  }

  // Handle enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isActivating) {
      handleActivation()
    }
  }

  // Handle input change and clear error
  const handleInputChange = (e) => {
    setLicenseCode(e.target.value)
    if (errorMessage) {
      setErrorMessage("")
    }
  }

  return (
    <div className="md:bg-paper md:rounded-[24px] md:shadow-custom_card md:p-6">
      <Typography 
        component="h2"
        variant="h5" 
        className="mb-6 text-text"
      >
        {texts?.activate_license_code || "Activate your License Code"}
      </Typography>

      <div className="mb-6">
        <Typography 
          variant="body_20" 
          className="font-medium text-text mt-0 mb-1 ml-3"
        >
          {texts?.license_code || "License Code"}
        </Typography>

        <div className="w-full flex flex-col md:flex-row items-start gap-6">
          <div className="w-full max-w-md flex-1">
            <Input
              value={licenseCode}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={texts?.license_code_placeholder || "Enter your license code (e.g., XXXX-XXXX-XXXX)"}
              className="w-full"
              disabled={isActivating}
            />

              {errorMessage && (
                <div className="h-12 mt-2">
                  <Typography 
                    variant="info_16" 
                    className="text-error my-0"
                  >
                    {errorMessage}
                  </Typography>
                </div>
              )}
            
          </div>

          <Button
            variant="primary_s"
            onClick={handleActivation}
            disabled={isActivating}
          >
            {isActivating ? texts?.license_activating_btn || "Activating..." : texts?.license_activate_btn || "Activate License"}
          </Button>
        </div>
      </div>

      {/* Instructions */}
      <div className="space-y-4">
        <Typography 
          variant="body_20" 
          className="font-medium text-text my-0"
        >
          {texts?.after_activation_note_title || "After activating your license:"}
        </Typography>
        
        <ul className="space-y-3 text-sm leading-relaxed text-text_secondary list-disc list-inside">
          <li>{texts?.after_activation_list_entry_1 || "Your account type will be updated to reflect your license benefits"}</li>

          <li>{texts?.after_activation_list_entry_2 || "Simply login with this account on your devices to register them with this license automatically"}</li>
          
          <li>{texts?.after_activation_list_entry_3 || "You can remove devices from your license to manage your available seats effectively"}</li>
          
          <li>{texts?.after_activation_list_entry_4 || "If your license supports multiple users, you can share your license key with trusted users who will use seats under your license"}</li>
          
          <li>{texts?.after_activation_list_entry_5 || "You can always remove users from your license when managing multi-user license access"}</li>
        </ul>
      </div>
    </div>
  )
}

export default LicenseActivation